import {
  ID,
  newTaskSuffix,
  rootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  TaskType,
  Timestamp,
  TypeSections,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

export type BuildTypeTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  tasksLabel: string;
  habitsLabel: string;
  rootLabel: string;
  newTaskLabel: string;
  newHabitLabel: string;
};

export const buildTypeTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  tasksLabel,
  habitsLabel,
  rootLabel,
  newTaskLabel,
  newHabitLabel,
}: BuildTypeTreeOptions): Record<ID, TaskListTreeItem> => {
  const allTreeItems: Record<ID, TaskListTreeItem> = {
    [TypeSections.Tasks]: {
      id: TypeSections.Tasks,
      type: TaskListItemTypes.Type,
      name: tasksLabel,
      children: [],
      isFolder: true,
    },
    [TypeSections.Habits]: {
      id: TypeSections.Habits,
      type: TaskListItemTypes.Type,
      name: habitsLabel,
      children: [],
      isFolder: true,
    },
  };

  const todoTasks = tasks.filter((task) => task.type !== TaskType.Habit);
  const sortedTodoTasks = getSortedTasks(todoTasks, {
    today,
    weekStartsOn,
    order:
      sortingMode === SortingMode.Custom
        ? customOrder?.[TypeSections.Tasks] ?? []
        : [],
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  });

  const habitTasks = tasks.filter((task) => task.type === TaskType.Habit);
  const sortedHabitTasks = getSortedTasks(habitTasks, {
    today,
    weekStartsOn,
    order:
      sortingMode === SortingMode.Custom
        ? customOrder?.[TypeSections.Habits] ?? []
        : [],
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  });

  sortedTodoTasks.forEach((task) => {
    if (allTreeItems[TypeSections.Tasks]) {
      allTreeItems[TypeSections.Tasks].children?.push(task.id);
    }
    allTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  });

  if (!hideNewTaskRow) {
    if (
      allTreeItems[TypeSections.Tasks] &&
      (showEmptyGroups || allTreeItems[TypeSections.Tasks].children?.length)
    ) {
      const newTaskItemId = `${TypeSections.Tasks}${newTaskSuffix}`;
      allTreeItems[TypeSections.Tasks].children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    }
  }

  sortedHabitTasks.forEach((task) => {
    if (allTreeItems[TypeSections.Habits]) {
      allTreeItems[TypeSections.Habits].children?.push(task.id);
    }
    allTreeItems[task.id] = {
      id: task.id,
      name: task.name,
      type: TaskListItemTypes.Task,
    };
  });

  if (!hideNewTaskRow) {
    if (
      allTreeItems[TypeSections.Habits] &&
      (showEmptyGroups || allTreeItems[TypeSections.Habits].children?.length)
    ) {
      const newHabitItemId = `${TypeSections.Habits}${newTaskSuffix}`;
      allTreeItems[TypeSections.Habits].children?.push(newHabitItemId);
      allTreeItems[newHabitItemId] = {
        id: newHabitItemId,
        name: newHabitLabel,
        type: TaskListItemTypes.NewTask,
      };
    }
  }

  const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
  const topLevelIds = Object.keys(filteredTreeItems).filter((id) =>
    [TypeSections.Tasks, TypeSections.Habits].includes(id as TypeSections),
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelIds,
    },
    ...filteredTreeItems,
  };
};
